// The cities served by one-way RV routes. The homepage one-way banner is only
// shown to visitors within ~50 miles of one of these cities, so the CTA leads to
// a route within reach (V2 — reduces the sad-path that hurt CVR).
//
// We match on the visitor's lat/lng (from /geodata/my-ip) rather than the city
// name, because MaxMind resolves an IP to its specific municipality — a renter in
// Santa Monica or Paradise, NV would never match the string "Los Angeles" or
// "Las Vegas". A radius around each city center covers the whole metro area
// automatically and needs no suburb list to maintain.
type TOneWayCity = {
  name: string;
  lat: number;
  lng: number;
};

const ONE_WAY_ROUTE_CITIES: TOneWayCity[] = [
  { name: 'Los Angeles, CA', lat: 34.0522, lng: -118.2437 },
  { name: 'San Francisco, CA', lat: 37.7749, lng: -122.4194 },
  { name: 'Dallas, TX', lat: 32.7767, lng: -96.797 },
  { name: 'Denver, CO', lat: 39.7392, lng: -104.9903 },
  { name: 'Las Vegas, NV', lat: 36.1699, lng: -115.1398 },
  { name: 'Miami, FL', lat: 25.7617, lng: -80.1918 },
  { name: 'New York City, NY', lat: 40.7128, lng: -74.006 },
  { name: 'Phoenix, AZ', lat: 33.4484, lng: -112.074 },
  { name: 'Salt Lake City, UT', lat: 40.7608, lng: -111.891 },
  { name: 'Seattle, WA', lat: 47.6062, lng: -122.3321 },
  { name: 'Vancouver, BC', lat: 49.2827, lng: -123.1207 },
  { name: 'Calgary, AB', lat: 51.0447, lng: -114.0719 },
  { name: 'Anchorage, AK', lat: 61.2181, lng: -149.9003 },
  { name: 'Atlanta, GA', lat: 33.749, lng: -84.388 },
  { name: 'Boston, MA', lat: 42.3601, lng: -71.0589 },
  { name: 'Bozeman, MT', lat: 45.677, lng: -111.0429 },
  { name: 'Chicago, IL', lat: 41.8781, lng: -87.6298 },
  { name: 'Houston, TX', lat: 29.7604, lng: -95.3698 },
  { name: 'Nashville, TN', lat: 36.1627, lng: -86.7816 },
  { name: 'New Orleans, LA', lat: 29.9511, lng: -90.0715 },
  { name: 'Orlando, FL', lat: 28.5383, lng: -81.3792 },
  { name: 'Portland, OR', lat: 45.5152, lng: -122.6784 },
  { name: 'San Diego, CA', lat: 32.7157, lng: -117.1611 },
  { name: 'Montreal, QC', lat: 45.5019, lng: -73.5674 },
  { name: 'Toronto, ON', lat: 43.6532, lng: -79.3832 },
];

const RADIUS_MILES = 50;
const EARTH_RADIUS_MILES = 3958.8;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

// Great-circle (haversine) distance in miles between two lat/lng points.
const distanceInMiles = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;

  return EARTH_RADIUS_MILES * 2 * Math.asin(Math.sqrt(a));
};

export const isNearOneWayCity = (lat?: number, lng?: number): boolean => {
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return false;
  }

  return ONE_WAY_ROUTE_CITIES.some(
    city => distanceInMiles(lat, lng, city.lat, city.lng) <= RADIUS_MILES,
  );
};
